import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { CarrierPriceListCatalogItem } from '@magimp/shared';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  ToolbarActions,
} from '../../../ui/index.js';
import { listPriceLists } from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';

// WHY: legacy rows pre-date the `fileContentType` discriminator and are
// represented by an omitted/undefined field. Treat them as images so old
// previews keep working without forcing a backfill migration (see #586).
function isPdfRow(row: CarrierPriceListCatalogItem): boolean {
  return row.fileContentType === 'application/pdf';
}

export function PriceListsPage() {
  const { t } = useTranslation(['priceLists', 'common']);
  const [q, setQ] = useState('');
  const [openItem, setOpenItem] = useState<CarrierPriceListCatalogItem | null>(null);
  const query = useQuery({
    queryKey: ['price-lists', q],
    queryFn: () => listPriceLists(q.trim() ? { q: q.trim() } : {}),
  });

  function activateRow(row: CarrierPriceListCatalogItem): void {
    if (isPdfRow(row)) {
      // INVARIANT: PDF rows open the signed object URL in a new tab via
      // browser-native PDF handling. The image modal is skipped because
      // <img src> cannot render PDFs and would surface a broken icon.
      window.open(row.imageDownloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setOpenItem(row);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('priceLists:title')}
        </h1>
        <ToolbarActions>
          <Input value={q} onChange={setQ} icon="search" placeholder={t('priceLists:search')} />
          <Button icon="refresh-cw" kind="ghost" onClick={() => void query.refetch()}>
            {t('common:actions.reload')}
          </Button>
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data.items}
          onSelect={activateRow}
          emptyState={<EmptyState title={t('common:states.emptyTitle')} text={t('empty')} />}
          columns={[
            {
              key: 'preview',
              label: t('columns.preview'),
              width: 92,
              render: (row) => <PreviewCell row={row} />,
            },
            { key: 'carrierName', label: t('columns.carrier'), sortable: true },
            { key: 'name', label: t('columns.name'), sortable: true },
          ]}
        />
      )}
      {openItem && (
        <Modal
          open
          width={820}
          title={t('modalTitle', { carrier: openItem.carrierName, name: openItem.name })}
          onClose={() => setOpenItem(null)}
        >
          <img
            src={openItem.imageDownloadUrl}
            alt=""
            style={{ width: '100%', maxHeight: 'calc(100vh - 180px)', objectFit: 'contain' }}
          />
        </Modal>
      )}
    </div>
  );
}

function PreviewCell({ row }: { row: CarrierPriceListCatalogItem }) {
  const { t } = useTranslation('priceLists');
  if (isPdfRow(row)) {
    return (
      <div
        aria-label={t('fileLabel.pdf')}
        style={{
          width: 64,
          height: 44,
          borderRadius: 'var(--r-1)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--fg-2)',
          letterSpacing: '0.4px',
        }}
      >
        {t('fileLabel.pdf')}
      </div>
    );
  }
  return (
    <img
      src={row.imageDownloadUrl}
      alt=""
      style={{
        width: 64,
        height: 44,
        objectFit: 'cover',
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--border-1)',
      }}
    />
  );
}
